/**
 * Chain configuration for bridge functionality
 * Supports Base and BNB Chain for WELEPHANT bridging
 */

// Alchemy API key from environment (optional)
const ALCHEMY_API_KEY = import.meta.env.VITE_ALCHEMY_API_KEY as string | undefined

// DEV_MODE check for Hardhat support
const DEV_MODE = import.meta.env.VITE_DEV_MODE === 'true'

// Build RPC URLs with Alchemy as preferred if API key is set
function buildRpcUrls(chainName: string, alchemyEndpoint: string, fallbacks: string[]): string[] {
  if (ALCHEMY_API_KEY) {
    const alchemyUrl = `${alchemyEndpoint}${ALCHEMY_API_KEY}`
    const urls = [alchemyUrl, ...fallbacks]
    console.log(`[Chains] ${chainName} RPC endpoints:`, [
      `${alchemyEndpoint}***${ALCHEMY_API_KEY.slice(-4)}`,
      ...fallbacks
    ])
    return urls
  }
  console.log(`[Chains] ${chainName} RPC endpoints (no Alchemy key):`, fallbacks)
  return fallbacks
}

// Build WebSocket URL with Alchemy as preferred if API key is set
function buildWebSocketUrl(chainName: string, alchemyWsEndpoint: string, fallback?: string): string | undefined {
  if (ALCHEMY_API_KEY) {
    const wsUrl = `${alchemyWsEndpoint}${ALCHEMY_API_KEY}`
    console.log(`[Chains] ${chainName} WebSocket endpoint: ${alchemyWsEndpoint}***${ALCHEMY_API_KEY.slice(-4)}`)
    return wsUrl
  }
  if (fallback) {
    console.log(`[Chains] ${chainName} WebSocket endpoint (fallback): ${fallback}`)
    return fallback
  }
  console.log(`[Chains] ${chainName} WebSocket endpoint: not available (no Alchemy key)`)
  return undefined
}

export interface ChainConfig {
  id: number
  name: string
  shortName: string
  nativeCurrency: {
    name: string
    symbol: string
    decimals: number
  }
  rpcUrls: string[]
  wsUrl?: string // WebSocket URL for real-time event subscriptions
  blockExplorerUrls: string[]
  iconColor: string
  // Wormhole chain identifier
  wormholeChainId: number
  wormholeChainName: string
}

// BNB Chain (BSC Mainnet)
export const BNB_CHAIN: ChainConfig = {
  id: 56,
  name: 'BNB Smart Chain',
  shortName: 'BNB',
  nativeCurrency: {
    name: 'BNB',
    symbol: 'BNB',
    decimals: 18,
  },
  rpcUrls: buildRpcUrls(
    'BNB',
    'https://bnb-mainnet.g.alchemy.com/v2/',
    ['https://bsc-dataseed.binance.org/', 'https://bsc-dataseed1.binance.org/']
  ),
  wsUrl: buildWebSocketUrl('BNB', 'wss://bnb-mainnet.g.alchemy.com/v2/'),
  blockExplorerUrls: ['https://bscscan.com/'],
  iconColor: '#F0B90B',
  wormholeChainId: 4, // Wormhole's BSC chain ID
  wormholeChainName: 'Bsc',
}

// Base Mainnet
export const BASE_CHAIN: ChainConfig = {
  id: 8453,
  name: 'Base',
  shortName: 'Base',
  nativeCurrency: {
    name: 'Ethereum',
    symbol: 'ETH',
    decimals: 18,
  },
  rpcUrls: buildRpcUrls(
    'Base',
    'https://base-mainnet.g.alchemy.com/v2/',
    ['https://mainnet.base.org', 'https://base.publicnode.com']
  ),
  wsUrl: buildWebSocketUrl('Base', 'wss://base-mainnet.g.alchemy.com/v2/'),
  blockExplorerUrls: ['https://basescan.org/'],
  iconColor: '#0052FF',
  wormholeChainId: 30, // Wormhole's Base chain ID
  wormholeChainName: 'Base',
}

// All supported chains for bridging
export const BRIDGE_CHAINS: ChainConfig[] = [BASE_CHAIN, BNB_CHAIN]

// Get chain by ID
export function getChainById(chainId: number): ChainConfig | undefined {
  return BRIDGE_CHAINS.find(chain => chain.id === chainId)
}

// Get chain by Wormhole chain ID
export function getChainByWormholeId(wormholeChainId: number): ChainConfig | undefined {
  return BRIDGE_CHAINS.find(chain => chain.wormholeChainId === wormholeChainId)
}

// WELEPHANT token addresses on each chain
export const WELEPHANT_ADDRESSES: Record<number, string> = {
  [BNB_CHAIN.id]: '0x241F21dF529C05289A00dAfeCEa10139A287cDCa',
  [BASE_CHAIN.id]: '0x7d03acf64a1a64bff9d24850011cca93c78a22e7',
}

// Multicall3 canonical address (same on most EVM chains)
// See: https://www.multicall3.com/
export const MULTICALL3_ADDRESS = '0xcA11bde05977b3631167028862bE2a173976CA11'

export const MULTICALL3_ADDRESSES: Record<number, string> = {
  [BNB_CHAIN.id]: MULTICALL3_ADDRESS,
  [BASE_CHAIN.id]: MULTICALL3_ADDRESS,
}

// Get WELEPHANT address for a chain
export function getWelephantAddress(chainId: number): string | undefined {
  return WELEPHANT_ADDRESSES[chainId]
}

// Get Multicall3 address for a chain
export function getMulticall3Address(chainId: number): string | undefined {
  return MULTICALL3_ADDRESSES[chainId]
}

// Network params for wallet_addEthereumChain
export function getAddChainParams(chain: ChainConfig) {
  return {
    chainId: `0x${chain.id.toString(16)}`,
    chainName: chain.name,
    nativeCurrency: chain.nativeCurrency,
    rpcUrls: chain.rpcUrls,
    blockExplorerUrls: chain.blockExplorerUrls,
  }
}

// Format chain ID to hex for wallet requests
export function chainIdToHex(chainId: number): string {
  return `0x${chainId.toString(16)}`
}

// Hardhat local WebSocket URL
const HARDHAT_WS_URL = 'ws://127.0.0.1:8545'
const HARDHAT_CHAIN_ID = 1337

/**
 * Get WebSocket URL for a chain
 * In DEV_MODE, returns Hardhat WebSocket URL
 * In production, returns Alchemy WebSocket URL for BNB chain
 */
export function getWebSocketUrl(chainId?: number): string | undefined {
  // In dev mode with Hardhat chain, use local WebSocket
  if (DEV_MODE && (!chainId || chainId === HARDHAT_CHAIN_ID)) {
    return HARDHAT_WS_URL
  }

  // In production or when explicitly requesting a specific chain
  const targetChainId = chainId || 56 // Default to BNB chain in production
  const chain = getChainById(targetChainId)
  return chain?.wsUrl
}

/**
 * Check if WebSocket support is available for the current environment
 */
export function isWebSocketAvailable(chainId?: number): boolean {
  return !!getWebSocketUrl(chainId)
}
